import React, { useEffect, useMemo } from 'react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
  Box,
  Divider,
  Flex,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  SimpleGrid,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { addSeconds } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { BsThreeDots } from 'react-icons/bs';
import { useSelector } from 'react-redux';

import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';

import { DataSummaryCard } from '../../../../common/components/DataSummaryCard';
import { OptionSelectAlertDrawer } from '../../../../common/components/OptionSelectAlertDrawer';
import { SlidingAnimationPageBase } from '../../../../common/components/SlidingAnimationPageBase';
import { useParamBackUrl } from '../../../../common/hooks/use-param-back-url';
import { selectInverters, selectSite } from '../../../site/siteSlice';
import { getFormattedTime } from '../../settings/settings-helpers';
import { CustomVppIcon } from '../CustomVPPIcon';
import { getProgramOfferByStatus } from '../helpers';
import { useGetVppEvents, useGetVppProgramOffers } from '../vppApi';
import { VPPHelpGuide } from '../VPPHelpGuide';
import { VPPEventListItem } from './VPPEventListItem';

export function ActiveProgram() {
  const { timezone } = useSelector(selectSite);
  const [{ id: inverterId }] = useSelector(selectInverters);
  const {
    programOffers,
    isLoading: isProgramOffersLoading,
    isError: isProgramOffersError,
  } = useGetVppProgramOffers(inverterId);
  const { data: vppEvents, isLoading: isVppEventsLoading, isError: isEventsError } = useGetVppEvents(inverterId);
  const isLoaded = !isProgramOffersLoading && !isVppEventsLoading;
  const isError = isProgramOffersError || isEventsError;

  const activeProgram = useMemo(() => getProgramOfferByStatus(programOffers, 'Enrolled'), [programOffers]);
  const programVppEvents = useMemo(() => {
    const activeProgramBatteryId = activeProgram?.equipment_programs?.find(({ status }) => status === 'Enrolled')
      ?.equipment.m_rid;
    return vppEvents?.find(({ m_rid: equipmentId }) => activeProgramBatteryId === equipmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProgram, vppEvents, isLoaded]);

  const textColor = useColorModeValue('customGrey.900', 'customGrey.300');
  const inactiveEventiconColor = useColorModeValue('primaryBranding.600', 'primaryBranding.200');
  const activeEventIconColor = useColorModeValue('schneiderSkyBlue.600', 'schneiderSkyBlue.200');
  const bgColor = useColorModeValue('#fff', '#111');
  const { isOpen: isOptionDrawerOpen, onOpen: onOpenOptionDrawer, onClose: onCloseOptionDrawer } = useDisclosure();
  const {
    isOpen: isLearnMoreModalOpen,
    onOpen: onLearnMoreModalOpen,
    onClose: onCloseLearnMoreModal,
  } = useDisclosure();
  const { t } = useTranslation();
  const toast = useToast({ duration: 3000, isClosable: true });
  const backURL = useParamBackUrl('../../../account/dashboard?direction=back');

  const activeVppEvent = useMemo(() => {
    return programVppEvents?.vpp_events.find(({ event_status: eventStatus }) => eventStatus === 'Pending');
  }, [programVppEvents]);
  const isVppEventInProgress = !!activeVppEvent;

  const scheduledVppEvents = useMemo(() => {
    return programVppEvents?.vpp_events.filter(({ event_status: eventStatus }) => eventStatus === 'Scheduled');
  }, [programVppEvents]);

  function handleSelectOptionDrawerOption(value: string) {
    if (value === 'LEARN_MORE') {
      onLearnMoreModalOpen();
    }
    onCloseOptionDrawer();
  }

  useEffect(() => {
    if (isLoaded) {
      if (isError) {
        toast.closeAll();
        toast({
          title: t('VPP.error getting vpp data'),
          description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
          status: 'error',
        });
      }
    }
  }, [isLoaded, isError, toast, t]);

  const activeEventStartDate = useMemo(
    () => (activeVppEvent ? toZonedTime(new Date(activeVppEvent?.interval.start), timezone) : undefined),
    [activeVppEvent, timezone]
  );

  const activeEventStartString = useMemo(
    () => (activeEventStartDate ? getFormattedTime(activeEventStartDate) : ''),
    [activeEventStartDate]
  );

  const activeEventEndString = useMemo(() => {
    if (activeEventStartDate && activeVppEvent) {
      const activeEventEndDate = addSeconds(activeEventStartDate, activeVppEvent.interval.duration);
      return getFormattedTime(activeEventEndDate);
    }
    return '';
  }, [activeEventStartDate, activeVppEvent]);

  return (
    <SlidingAnimationPageBase mx={4} mb={4} backURL={backURL} title={t('VPP.title')}>
      <Box data-testid="active-program">
        <Flex position="absolute" right={0}>
          <IconButton
            aria-label="vpp settings"
            variant="unstyled"
            height="auto"
            icon={<BsThreeDots size={26} />}
            data-testid="three-dots-btn"
            onClick={onOpenOptionDrawer}
          />
        </Flex>
        <OptionSelectAlertDrawer
          isOpen={isOptionDrawerOpen}
          onClose={onCloseOptionDrawer}
          onSelectOption={handleSelectOptionDrawerOption}
          options={[
            {
              value: 'LEARN_MORE',
              label: t('VPP.learn more'),
            },
          ]}
          title={t('VPP.more about vpp')}
        />
        <CustomVppIcon
          isLoaded={isLoaded}
          iconColor={isVppEventInProgress ? activeEventIconColor : inactiveEventiconColor}
        />

        <Heading size="lg" textAlign="center" my={2}>
          {activeProgram?.program.name}
        </Heading>

        <Text textAlign="center" color={textColor} lineHeight={1.2}>
          {isVppEventInProgress ? t('VPP.participating subtitle') : t('VPP.not participating subtitle')}
        </Text>
        <Text textAlign="center" color={textColor} mb={4} lineHeight={1.2}>
          {isVppEventInProgress ? t('VPP.participating description') : t('VPP.not participating description')}
        </Text>

        <Divider my={2} />

        {isVppEventInProgress && (
          <Box data-testid="active-event">
            <Text>{t('VPP.current event').toUpperCase()}</Text>
            <SimpleGrid gridGap={5} mt={2} mb={4} minChildWidth="45%">
              <DataSummaryCard title={t('Common.start time')} isLoaded={isLoaded} value={activeEventStartString} />
              <DataSummaryCard title={t('Common.end time')} isLoaded={isLoaded} value={activeEventEndString} />
            </SimpleGrid>
            <Divider my={2} />
          </Box>
        )}

        <Text>{t('VPP.upcoming events').toUpperCase()}</Text>
        {!isLoaded ? (
          <CenteredLoader text={`${t('Common.loading')}...`} mt={12} />
        ) : (
          scheduledVppEvents?.map((vppEvent) => (
            <VPPEventListItem
              key={vppEvent.m_rid}
              inverterId={inverterId}
              vppEventId={vppEvent.m_rid}
              vppEventStart={vppEvent.interval.start}
              vppEventDuration={vppEvent.interval.duration}
              programName={activeProgram?.program.name || ''}
            />
          ))
        )}

        <Modal
          scrollBehavior={'inside'}
          isOpen={isLearnMoreModalOpen}
          onClose={onCloseLearnMoreModal}
          size="full"
          motionPreset="slideInRight"
        >
          <ModalContent data-testid="learn-more-modal" backgroundColor={bgColor} top={'env(safe-area-inset-top)'}>
            <ModalCloseButton
              position={'absolute'}
              left={6}
              mt={1.5}
              fontSize={'17px'}
              data-testid="learn-more-modal-close-button"
              onClick={onCloseLearnMoreModal}
            >
              <ChevronLeftIcon w={'26px'} h={'26px'} />
            </ModalCloseButton>
            <ModalBody>
              <VPPHelpGuide />
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </SlidingAnimationPageBase>
  );
}
